import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import {
  ISetCamera,
  requiredCameraProperties,
} from "../../../models/interactions/SetCamera";
import { SequenceNodeProps } from "./SequenceNodeProps";
import { useMemo } from "react";
import useSetCameraOptions, {
  findSetCameraOption,
} from "../../../hooks/SetCameraOptions.hook";
import NodeStringDropdown from "../components/NodeStringDropdown";
import CameraRenderPreview from "../../viewport3d/CameraRenderPreview";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import NodeToggle from "../components/NodeToggle";
import NodeNumberField from "../components/NodeNumberField";
import { UpdatePropertyOfInteraction } from "../../../app/slices/helpers/TrainingSliceHelper";
import { InteractionProperty } from "../../../models/InteractionProperty";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function SetCameraNodePreview(props: SequenceNodeProps) {
  const setCamera = props.interaction as ISetCamera;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const camerasMapped = useSetCameraOptions(setCamera);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "camera";

  const cameraDropdown = useMemo(() => {
    return (
      <div className="flex flex-col gap-[8px] noDrag nowheel">
        <div className="font-medium text-creator-text-sub text-[14px]">Camera</div>
        <div className="flex items-center gap-[8px]">
          <div className="flex-1 min-w-0">
            <NodeStringDropdown
              items={camerasMapped.map((c) => c.displayName)}
              selectedItem={
                findSetCameraOption(camerasMapped, setCamera)?.displayName ??
                "Not found"
              }
              onClick={(cameraName) => {
                const selectedCamera = camerasMapped.find(
                  (c) => c.displayName === cameraName,
                );
                updateInteraction(props.nodeId, {
                  ...setCamera,
                  cameraId: selectedCamera?.id ?? "",
                  takeLocationId: selectedCamera?.context.takeLocationId ?? "",
                } as ISetCamera);
              }}
            />
          </div>
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-camera-on-map-button"
            className={
              NODE_PICK_BUTTON_BASE +
              (isPickerActiveForNode
                ? NODE_PICK_BUTTON_ACTIVE
                : NODE_PICK_BUTTON_IDLE)
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "camera",
                  title: "Pick camera position",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        </div>
      </div>
    );
  }, [
    camerasMapped,
    dispatch,
    isPickerActiveForNode,
    props.nodeId,
    setCamera,
    updateInteraction,
  ]);

  const instantProperty = useMemo(() => {
    return setCamera.properties?.find((p) => p.id === "instant");
  }, [setCamera.properties]);
  const speedProperty = useMemo(() => {
    return setCamera.properties?.find((p) => p.id === "speed");
  }, [setCamera.properties]);
  const isInstant = (instantProperty?.value as boolean) ?? false;

  const instantToggle = useMemo(
    () => (
      <NodeToggle
        label="Instant"
        value={isInstant}
        onChange={(instant) => {
          if (instantProperty) {
            const updatedProperty: InteractionProperty = {
              ...instantProperty,
              value: instant,
            };
            const updatedInteraction = UpdatePropertyOfInteraction(
              setCamera,
              updatedProperty,
            );
            updateInteraction(props.nodeId, updatedInteraction);
          }
        }}
      />
    ),
    [instantProperty, isInstant, setCamera, updateInteraction, props.nodeId],
  );

  const speedInput = useMemo(
    () => (
      <NodeNumberField
        label="Speed"
        value={(speedProperty?.value as number) ?? 1}
        disabled={isInstant}
        onChange={(speed) => {
          const baseProperty =
            speedProperty ??
            requiredCameraProperties.find(
              (property) => property.id === "speed",
            );
          if (!baseProperty) return;

          const updatedProperty: InteractionProperty = {
            ...baseProperty,
            value: speed,
          };

          const updatedInteraction = speedProperty
            ? UpdatePropertyOfInteraction(setCamera, updatedProperty)
            : ({
                ...setCamera,
                properties: [...(setCamera.properties ?? []), updatedProperty],
              } as ISetCamera);

          updateInteraction(props.nodeId, updatedInteraction);
        }}
      />
    ),
    [isInstant, props.nodeId, setCamera, speedProperty, updateInteraction],
  );

  return (
    <div className="flex flex-col w-full h-full gap-[12px] noDrag">
      {cameraDropdown}
      {/* The shot this node cuts to; no frame until a camera is chosen, to keep the node short. */}
      {setCamera.cameraId && (
        <CameraRenderPreview cameraId={setCamera.cameraId} className="w-full" />
      )}
      {instantToggle}
      {speedInput}
    </div>
  );
}
